/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { RootState } from '../../store';
import { saveLocationThunk, fetchLocationsThunk } from '../../actions/location/LocationActions';
import { useLocationAccountOptions, useSaveLocation } from './useLocationEdit';
import { showSuccessThunk, showErrorThunk } from '@spike/notifications-action';
import { canManageOrganizations } from '../../auth/Auth';
import { SaveLocation, Location, LocationPagination } from '../../model/location';
import { buildPath, LocationDetailPath } from '../../routes/Paths';

type ModalMode = 'add' | 'edit' | 'view';

interface LocationsContextValue {
    // Data
    locations: Location[];
    pagination: LocationPagination;
    filteredLocations: Location[];
    accountOptions: Array<{ label: string; value: string; parentOrganizationId?: string | null }>;
    loading: { locations: boolean; initialLoading: boolean };
    hasInitialized: boolean;

    // Filter state
    selectedOption: string;
    searchText: string;

    // Modal state
    isAddLocationModalOpen: boolean;
    editingLocation: Location | null;
    modalMode: ModalMode;
    duplicatingLocation: Location | null;

    // Permissions
    canAddLocations: boolean;

    //status toggle state and habdler
    handleToggleStatus: (action: string, location: Location) => void;
    showConfirm: { show: boolean; action: string; location: Location | null };
    setShowConfirm: React.Dispatch<React.SetStateAction<{ show: boolean; action: string; location: Location | null }>>;

    // Filter handlers
    handleStatusChange: (option: string) => void;
    handleSearchChange: (text: string) => void;

    // Pagnination handlers
    handlePageChange: (page: number) => void;
    handlePageSizeChange: (size: number) => void;

    // Location handlers
    handleAddLocation: () => void;
    handleEditLocation: (location: Location) => void;
    handleDeactivateLocation: (location: Location) => void;
    handleActivateLocation: (location: Location) => void;
    handleDeleteLocation: (location: Location) => void;
    handleLocationClick: (location: Location) => void;
    handleDuplicateLocation: (location: Location) => void;

    // Modal handlers
    handleCloseModal: () => void;
    handleSaveLocation: (locationData: SaveLocation) => Promise<void>;
    handleCloseDuplicate: () => void;
    handleDuplicateFinished: () => void;
    handleViewDuplicatedLocation: (locationId: number) => void;
}

const LocationsContext = createContext<LocationsContextValue | undefined>(undefined);

interface LocationsProviderProps {
    children: ReactNode;
}

export const LocationsProvider: React.FC<LocationsProviderProps> = ({ children }) => {
    const dispatch = useDispatch();
    const history = useHistory();

    // Redux state
    const { organizations } = useSelector((state: RootState) => state.organizations);
    const { locations, loading, pagination } = useSelector((state: RootState) => state.locations);

    // Local state - Filters
    const [selectedOption, setSelectedOption] = useState('active');
    const [searchText, setSearchText] = useState('');

    // Local state - Pagination
    const [pageInfo, setPageInfo] = useState({
        page: 1,
        size: 10
    });

    // Local state - Modals
    const [isAddLocationModalOpen, setIsAddLocationModalOpen] = useState(false);
    const [editingLocation, setEditingLocation] = useState<Location | null>(null);
    const [modalMode, setModalMode] = useState<ModalMode>('add');
    const [duplicatingLocation, setDuplicatingLocation] = useState<Location | null>(null);

    // Change status confirmation
    const [showConfirm, setShowConfirm] = React.useState({
        show: false,
        action: '',
        location: null as Location | null
    });
    const handleToggleStatus = (action: string, location: Location) => {
        setShowConfirm({ show: true, action, location });
    };
    const resetConfirm = () => {
        setShowConfirm({ show: false, action: '', location: null });
    };

    // Fetch locations function
    const fetchLocations = useCallback(() => {
        dispatch(fetchLocationsThunk(pageInfo.page, pageInfo.size, searchText) as any);
    }, [dispatch, pageInfo.page, pageInfo.size, searchText]);

    // Fetch locations when pagination changes
    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    // Filter accounts with parent_organization_id !== null (child accounts)
    const childAccounts = useMemo(
        () => organizations.filter(org => org.parentOrganizationId !== null),
        [organizations]
    );

    // Account options for the AddLocationModal (fetches organizations if not loaded)
    const accountOptions = useLocationAccountOptions();
    const saveLocation = useSaveLocation();

    // Track if locations have been initialized (at least one fetch attempt)
    const hasInitialized = useMemo(() => locations.length > 0 || !loading, [locations.length, loading]);

    // Filter locations based on selected option and search text
    const filteredLocations = useMemo(
        () =>
            locations
                .filter(location => {
                    const matchesStatus =
                        selectedOption === 'all' ||
                        (selectedOption === 'active' && location.active) ||
                        (selectedOption === 'inactive' && !location.active);

                    // Get account name for search
                    const accountName = organizations.find(org => org.id === location.organizationId)?.name || '';

                    // Get address from addresses_attributes
                    const addressData = location.addressesAttributes?.[0];
                    const addressSearchText = [
                        addressData?.addressLineOne,
                        addressData?.addressLineTwo,
                        addressData?.city,
                        addressData?.state,
                        addressData?.zipcode
                    ]
                        .filter(Boolean)
                        .join(' ')
                        .toLowerCase();

                    const matchesSearch =
                        !searchText ||
                        location.businessName.toLowerCase().includes(searchText.toLowerCase()) ||
                        accountName.toLowerCase().includes(searchText.toLowerCase()) ||
                        addressSearchText.includes(searchText.toLowerCase()) ||
                        location.email.toLowerCase().includes(searchText.toLowerCase());

                    return matchesStatus && matchesSearch;
                })
                .sort((a, b) => {
                    // Get account names for both locations
                    const accountNameA = organizations.find(org => org.id === a.organizationId)?.name || '';
                    const accountNameB = organizations.find(org => org.id === b.organizationId)?.name || '';

                    // Sort descending (B to A)
                    return accountNameA.localeCompare(accountNameB);
                }),
        [locations, selectedOption, searchText, organizations]
    );

    // Permissions
    const canAddLocations = useMemo(() => canManageOrganizations(), []);

    // Filter handlers
    const handleStatusChange = useCallback((option: string) => {
        setSelectedOption(option);
    }, []);

    const handleSearchChange = useDebouncedCallback((text: string) => {
        setSearchText(text);
        setPageInfo(prev => ({ ...prev, page: 1 })); // Reset to first page on search
    }, 200);

    // Pagnination handlers
    const handlePageChange = useCallback((page: number) => {
        setPageInfo(prev => ({ ...prev, page }));
    }, []);

    const handlePageSizeChange = useCallback((size: number) => {
        setPageInfo(prev => ({ page: 1, size }));
    }, []);

    // Location handlers
    const handleAddLocation = useCallback(() => {
        setEditingLocation(null);
        setModalMode('add');
        setIsAddLocationModalOpen(true);
    }, []);

    const handleEditLocation = useCallback((location: Location) => {
        setEditingLocation(location);
        setModalMode('edit');
        setIsAddLocationModalOpen(true);
    }, []);

    const handleDeactivateLocation = useCallback(
        async (location: Location) => {
            resetConfirm();
            try {
                const result = await dispatch(
                    saveLocationThunk({
                        id: location.id,
                        active: false
                    } as any) as any
                );

                // Only show success message if the thunk succeeded
                if (result) {
                    dispatch(showSuccessThunk('Location deactivated successfully') as any);
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error deactivating location:', error);
                dispatch(showErrorThunk('Failed to deactivate location. Please try again.') as any);
            }
        },
        [dispatch]
    );

    const handleActivateLocation = useCallback(
        async (location: Location) => {
            resetConfirm();
            try {
                const result = await dispatch(
                    saveLocationThunk({
                        id: location.id,
                        active: true
                    } as any) as any
                );

                // Only show success message if the thunk succeeded
                if (result) {
                    dispatch(showSuccessThunk('Location activated successfully') as any);
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error activating location:', error);
                dispatch(showErrorThunk('Failed to activate location. Please try again.') as any);
            }
        },
        [dispatch]
    );

    const handleDeleteLocation = useCallback((location: Location) => {
        // TODO: Implement API call to delete location
        if (window.confirm(`Are you sure you want to delete "${location.businessName}"?`)) {
            // eslint-disable-next-line no-console
            console.log('Delete location:', location);
        }
    }, []);

    const handleLocationClick = useCallback(
        (location: Location) => {
            history.push(buildPath(LocationDetailPath, { id: location.id }));
        },
        [history]
    );

    const handleDuplicateLocation = useCallback((location: Location) => {
        setDuplicatingLocation(location);
    }, []);

    // Modal handlers
    const handleCloseModal = useCallback(() => {
        setIsAddLocationModalOpen(false);
        setEditingLocation(null);
    }, []);

    const handleSaveLocation = useCallback(
        async (locationData: SaveLocation) => {
            // Redux state will be updated automatically by the thunk; keep modal open on error so user can try again
            if ((await saveLocation(locationData)) !== null) {
                setIsAddLocationModalOpen(false);
                setEditingLocation(null);
            }
        },
        [saveLocation]
    );

    const handleCloseDuplicate = useCallback(() => {
        setDuplicatingLocation(null);
    }, []);

    const handleViewDuplicatedLocation = useCallback(
        (locationId: number) => {
            history.push(buildPath(LocationDetailPath, { id: locationId }));
        },
        [history]
    );

    const value: LocationsContextValue = {
        // Data
        locations,
        pagination: {
            page: pageInfo.page,
            size: pageInfo.size,
            totalPages: pagination?.totalPages ?? 1,
            totalRecords: pagination?.totalRecords ?? 0
        },
        filteredLocations,
        accountOptions,
        loading: { locations: loading, initialLoading: loading && !hasInitialized },
        hasInitialized,

        // Filter state
        selectedOption,
        searchText,

        // Modal state
        isAddLocationModalOpen,
        editingLocation,
        modalMode,
        duplicatingLocation,

        // Permissions
        canAddLocations,

        // Filter handlers
        handleStatusChange,
        handleSearchChange,

        // Pagnination handlers
        handlePageChange,
        handlePageSizeChange,

        // Location handlers
        handleAddLocation,
        handleEditLocation,
        handleDeactivateLocation,
        handleActivateLocation,
        handleDeleteLocation,
        handleLocationClick,
        handleDuplicateLocation,

        //status toggle state and handler
        showConfirm,
        setShowConfirm,
        handleToggleStatus,

        // Modal handlers
        handleCloseModal,
        handleSaveLocation,
        handleCloseDuplicate,
        // Reloads the current page so the new location shows up
        handleDuplicateFinished: fetchLocations,
        handleViewDuplicatedLocation
    };

    return <LocationsContext.Provider value={value}>{children}</LocationsContext.Provider>;
};

export const useLocationsContext = (): LocationsContextValue => {
    const context = useContext(LocationsContext);
    if (!context) {
        throw new Error('useLocationsContext must be used within a LocationsProvider');
    }
    return context;
};

export default LocationsProvider;
